import { ref, onMounted, onUnmounted, onBeforeUnmount } from 'vue'
import { useAuthStore } from '@/modules/auth/authStore'
import { useRouter } from 'vue-router'
import EventBus from '@/libs/AppEventBus'
import { useStore as useNotificationStore } from '@/modules/notification/store'
import { fromNow } from '@/utils/formatter'
import ability from '@/libs/acl/ability'
import { teardownEcho } from '@/libs/echo'

export default function useNotification() {
    const authStore = useAuthStore()
    const router = useRouter()
    const userPanel = ref()
    const notifications = ref([])
    const noticount = ref(0)
    const user = authStore.getUserData
    const notificationStore = useNotificationStore()
    const page = ref(0)
    const perPage = ref(20)
    const lastPage = ref(false)
    const isLoading = ref(false)
    const notiId = ref(null)

    // Chat notifications also refresh the chat lists / badges (they only track the chats loaded on page load)
    const emitChatRefresh = (type) => {
        if (type == 'message') EventBus.emit('appointment-chat-message')
        if (type == 'support-chat-message' || type == 'support-chat') EventBus.emit('support-chat-message')
    }

    const onDoctorNotification = (e) => {
        if (user.id == e.notification.notifiable) {
            appendNoti(e)
        }
        emitChatRefresh(e.notification.type)
    }

    const onPortalNotification = (e) => {
        if (user.corporate_ids.length == 0 && e.notification.notifiable == 'portal') {
            const action = e.notification.permission.substring(0, e.notification.permission.indexOf('-'))
            if (ability.can(action, 'notifications')) {
                appendNoti(e)
            }
        }
    }

    // Pusher does not replay events sent while the connection was down (sleep, network switch):
    // reload the list and chat badges once it is back.
    let wasDisconnected = false
    const onConnectionStateChange = (states) => {
        if (states.current === 'unavailable' || states.current === 'disconnected' || states.current === 'failed') {
            wasDisconnected = true
        } else if (states.current === 'connected' && wasDisconnected) {
            wasDisconnected = false
            refreshNotifications()
            if (user.role == 'doctor') {
                EventBus.emit('appointment-chat-message')
                EventBus.emit('support-chat-message')
            }
        }
    }
    const pusherConnection = () => window.Echo?.connector?.pusher?.connection

    onMounted(() => {
        window.addEventListener('scroll', handleScroll)
        fetchNotifications()
        if (user.role == 'doctor') {
            window.Echo.private('notification.' + user.id).listen('.new.notification', onDoctorNotification)
        } else {
            window.Echo.private('notification.portal').listen('.new.notification', onPortalNotification)
        }
        pusherConnection()?.bind('state_change', onConnectionStateChange)
    })

    onUnmounted(() => {
        window.removeEventListener('scroll', handleScroll)
        // the topbar is re-mounted with the layout: don't stack handlers (double toasts)
        if (user.role == 'doctor') {
            window.Echo?.private('notification.' + user.id).stopListening('.new.notification', onDoctorNotification)
        } else {
            window.Echo?.private('notification.portal').stopListening('.new.notification', onPortalNotification)
        }
        pusherConnection()?.unbind('state_change', onConnectionStateChange)
    })

    onBeforeUnmount(() => {
        authStore.$dispose()
        notificationStore.$dispose()
    })

    const togglePanel = (event) => {
        userPanel.value.toggle(event)
        if (noticount.value !== 0) {
            markAsAll()
        }
    }

    const userLogout = () => {
        authStore.logout()
        window.Echo.private('comm.' + user.id).leave()
        window.Echo.private('notification.' + user.id).leave()
        window.Echo.private('notification.portal').leave()
        window.Echo.channel('appointment').leave()
        teardownEcho()
        router.replace({ name: 'login' })
    }

    const sound = require('@/assets/sounds/drop_don.mp3')
    const playNoti = () => {
        let notiSound = new Audio(sound)
        notiSound.addEventListener('canplaythrough', () => {
            notiSound.play()
        })
    }

    const appendNoti = (data) => {
        notifications.value = notifications.value.filter((val) => val.id !== data.notification.id)
        const newArr = notifications.value.slice()
        newArr.unshift(data.notification)
        notifications.value = newArr
        if (notifications.value[findIndexById(data.notification.id)].checked == 0) {
            noticount.value = noticount.value + 1
        }

        // notifyUser(JSON.parse(data.notification.data).title, JSON.parse(data.notification.data).message)
        // EventBus.emit('show-toast', {
        //     severity: 'info',
        //     summary: 'New notification !',
        //     detail: JSON.parse(data.notification.data).message
        // })

        EventBus.emit('show-toast', {
            severity: 'info',
            summary: JSON.parse(data.notification.data).title,
            detail: JSON.parse(data.notification.data).message
        })
        playNoti()
    }

    // const notifyUser = (title, message) => {
    //     // Check if the browser supports notifications
    //     if (!('Notification' in window)) {
    //         console.log('This browser does not support desktop notifications.')
    //     } else if (Notification.permission === 'granted') {
    //         // If permission is already granted, create a notification
    //         showBrowserNotification(title, message)
    //     } else if (Notification.permission !== 'denied') {
    //         // Otherwise, ask for permission first
    //         Notification.requestPermission().then((permission) => {
    //             if (permission === 'granted') {
    //                 showBrowserNotification(title, message)
    //             }
    //         })
    //     }
    // }

    // const showBrowserNotification = (title, message) => {
    //     new Notification(title, {
    //         body: message,
    //         icon: require('@/assets/images/logo-white.svg')
    //     })
    // }

    // const notifyUser = (title ,message) => {
    //     // Check if the browser supports notifications
    //     if (!('Notification' in window)) {
    //         console.log('This browser does not support desktop notifications.')
    //     } else if (Notification.permission === 'granted') {
    //         // If permission is already granted, create a notification
    //         showBrowserNotification(title, message)
    //     } else if (Notification.permission !== 'denied') {
    //         // Otherwise, ask for permission first
    //         Notification.requestPermission().then((permission) => {
    //             if (permission === 'granted') {
    //                 showBrowserNotification(title, message)
    //             }
    //         })
    //     }
    // }

    // const showBrowserNotification = (title, message) => {
    //     new Notification(title, {
    //         body: message,
    //         icon: require('@/assets/images/logo-white.svg')
    //     })
    //     playNoti()
    // }

    const fetchNotifications = async () => {
        isLoading.value = true
        page.value++
        //fetch API
        await notificationStore.fetchAll({
            page: page.value,
            per_page: perPage.value,
            request_from: 'portal',
            is_doctor_role: user.role == 'doctor'
        })
        //get response
        const response = notificationStore.getAllResponse
        //assign value
        if (response) {
            isLoading.value = false
            const data = response.data.notiList.data
            noticount.value = response.data.notiCount
            notifications.value.push(...data)
            lastPage.value = notifications.value.length >= response.data.notiList.total
        }
    }
    const refreshNotifications = async () => {
        page.value = 0
        lastPage.value = false
        notifications.value = []
        await fetchNotifications()
    }

    const markAsAll = async () => {
        await notificationStore.markAsAll()

        const response = notificationStore.getMarkAsAllResponse

        if (response) {
            noticount.value = 0
        }
    }

    const markCheck = async (id) => {
        await notificationStore.check({
            id: id
        })

        const response = notificationStore.getCheckResponse

        if (response) {
            notifications.value[findIndexById(id)].checked = 1
            if (notiId.value != id) {
                noticount.value = Math.max(noticount.value - 1, 0)
            }
            notiId.value = id
        }
    }

    const findIndexById = (id) => {
        let index = -1
        for (let i = 0; i < notifications.value.length; i++) {
            if (notifications.value[i].id === id) {
                index = i
                break
            }
        }

        return index
    }

    const deleteNoti = async (id) => {
        await notificationStore.delete({
            id: id
        })

        const response = notificationStore.getDeleteResponse

        if (response) {
            if (notifications.value[findIndexById(id)].checked == 1) {
                if (notiId.value != id) {
                    noticount.value = Math.max(noticount.value - 1, 0)
                }
            }
            notifications.value = notifications.value.filter((val) => val.id !== id)
            notiId.value = id
        }
    }

    const handleScroll = () => {
        if (userPanel.value) {
            userPanel.value.hide()
        }
    }

    const gotoLink = (id, path) => {
        if (path) {
            router.push({ path: '/' + path })
        }
    }

    return {
        user,
        userPanel,
        noticount,
        notifications,
        isLoading,
        lastPage,
        togglePanel,
        userLogout,
        fromNow,
        markCheck,
        deleteNoti,
        fetchNotifications,
        notificationStore,
        gotoLink,
        markAsAll
    }
}
